// Quick-find — put the cursor in whatever search box THIS page already has.
//
// The console has 36 distinct search inputs across its pages, each already wired to that
// page's own real (mostly server-side) filter. None of them was reachable from the
// keyboard: to narrow a list you had to leave the keyboard, find the box, and click it.
//
// So this deliberately does NOT introduce a new search engine or a new index. It finds the
// box the operator would have clicked and focuses it, which means every page gets the
// shortcut for free, including pages that don't exist yet, and the semantics are always
// exactly that page's own search — no second, subtly-different definition of "matches".
//
// Identified by the shape they actually have, because none of them is `type="search"` and
// adding an attribute to 36 files would be the same edit 36 times. In priority order:
//   1. `data-quick-find` — explicit opt-in, for a box this heuristic would miss
//   2. `type="search"`   — the standard, honoured if anyone starts using it
//   3. a placeholder that STARTS with search/filter/find — how every one of them reads
//      ("Search all events — name, organizer, venue…", "Filter these 25 loaded orders…")
// Anchored to the start so an unrelated field whose placeholder merely mentions the word
// (e.g. "Add a note visible only to the support team…") can never be mistaken for one.

const SEARCH_PLACEHOLDER = /^\s*(search|filter|find)\b/i;

const isVisible = (el) => {
  if (!el || el.disabled || el.readOnly) return false;
  // offsetParent is null for display:none (and for position:fixed, hence the rect check).
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

// Is this element inside a floating layer (modal, drawer, popover)? A search box in an open
// overlay is the one the operator means — the page behind it isn't what they're looking at.
const inOverlay = (el) => {
  for (let n = el.parentElement; n; n = n.parentElement) {
    const pos = getComputedStyle(n).position;
    if (pos === 'fixed' || pos === 'sticky') return pos === 'fixed';
  }
  return false;
};

/** Every search input currently on screen, overlay ones first, then DOM order. */
export const findPageSearchInputs = (root = document) => {
  const all = [...root.querySelectorAll('input')].filter((el) => {
    if (!isVisible(el)) return false;
    if (el.hasAttribute('data-quick-find')) return true;
    if (el.type === 'search') return true;
    const t = el.type || 'text';
    if (t !== 'text' && t !== 'search') return false;
    return SEARCH_PLACEHOLDER.test(el.placeholder || '');
  });
  return all.sort((a, b) => Number(inOverlay(b)) - Number(inOverlay(a)));
};

/**
 * Focus this page's search box and select whatever is already in it (so typing replaces the
 * previous query rather than appending to it — the thing you almost always want when you
 * reach for search a second time).
 *
 * Returns true if it found one. The caller decides what "no search box here" should do;
 * a shortcut that silently does nothing is worse than one that escalates.
 */
export const focusPageSearch = () => {
  const [input] = findPageSearchInputs();
  if (!input) return false;
  input.focus({ preventScroll: false });
  try { input.select(); } catch { /* not all inputs support select() */ }
  return true;
};
